import { useQuery } from '@tanstack/react-query';
import { customFetch } from '~/utils/customFetch';
import { Binary } from '~/utils/typescriptHelpers';

export const periodMap = {
  D: 'Daily',
  W: 'Weekly',
  M: 'Monthly',
  O: 'Total',
} as const;

type CapData = {
  tripped: Binary;
  remaining: string;
  amount: number;
  paced: Binary;
  period: 'D' | 'W' | 'M' | 'O';
  partner_id: null | number;
  start_date: null | string;
  partner_name: string | null;
  client_editable: Binary;
};

export type CapReturnData =
  | {
      [capId: number]: CapData;
    }
  | { error: string };

const getOverallCaps = async (id?: string | number, currency?: string) => {
  if (!id) {
    throw new Error('No client id');
  }
  const requestOptions = {
    params: {
      client_id: String(id),
    },
  };

  const data = await customFetch
    .get<CapReturnData>(`/client_caps.pl`, requestOptions)
    .then((result) => result.data);
  if (!data) {
    throw new Error('Error getting caps');
  }
  if ('error' in data) {
    throw new Error(data.error);
  }
  const displayData = Object.entries(data).map(([capId, cap]) => ({
    id: capId,
    startDate: cap.start_date,
    period: periodMap[cap.period],
    rawPeriod: cap.period,
    paced: cap.paced,
    cap: `${currency ?? ''}${cap.amount}`,
    rawCap: cap.amount as number | null,
    remaining: cap.remaining ? currency + cap.remaining : '',
    tripped: cap.tripped ? cap.tripped : 0,
    ...(cap.tripped ? { styles: { background: '#ffc8c8' } } : {}),
    partnerId: cap.partner_id,
    partnerName: cap.partner_id === -1 ? 'Dianomi Marketplace' : cap.partner_name,
    clientEditable: cap.client_editable,
    status: 'active',
  }));

  // Sort by partnerName with empty values first, then by period
  return displayData.sort((a, b) => {
    const aName = a.partnerName || '';
    const bName = b.partnerName || '';

    // First sort by partnerName
    if (!aName && !bName) {
      // If both are empty, sort by period
    } else if (!aName) {
      return -1; // a comes first
    } else if (!bName) {
      return 1; // b comes first
    } else if (aName !== bName) {
      return aName.localeCompare(bName); // Sort alphabetically
    }

    // If partnerNames are the same (or both empty), sort by period
    const periodOrder = { Daily: 1, Weekly: 2, Monthly: 3, Total: 4 };
    const aPeriodOrder = periodOrder[a.period as keyof typeof periodOrder] || 5;
    const bPeriodOrder = periodOrder[b.period as keyof typeof periodOrder] || 5;

    return aPeriodOrder - bPeriodOrder;
  });
};

export type FormattedCapsData = Awaited<ReturnType<typeof getOverallCaps>>;

export const useOverallCaps = (id?: string | number, currency?: string) => {
  return useQuery({
    queryKey: ['overallCaps', String(id), currency],
    queryFn: async () => getOverallCaps(id, currency),
    enabled: !!id,
  });
};
